import { Box, Theme, TypographyProps, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { CSSProperties, FC, ReactNode } from 'react';
import { WbCardHeader } from './WbCardHeader';

export type WbCardVariant = 'default' | 'secondary';

export interface WbCardProps {
  children?: ReactNode;
  title: ReactNode;
  icon?: JSX.Element;
  actions?: JSX.Element;
  leading?: JSX.Element;
  typographyProps?: TypographyProps;
  headerStyle?: CSSProperties;
  headerContentStyle?: CSSProperties;
  footer?: JSX.Element;
  toolsComponents?: JSX.Element;
  statusComponent?: JSX.Element;
  cardClassName?: string;
  cardStyle?: CSSProperties;
  footerStyle?: CSSProperties;
  toolsStyle?: CSSProperties;
  toolsBarStyle?: CSSProperties;
  fillContainer?: boolean;
  titleContainerStyle?: CSSProperties;
  hideHeader?: boolean;
  variant?: WbCardVariant;
}

type UseStylesProps = {
  hasTools: boolean;
  fillContainer: boolean;
};

const useStyles = makeStyles<Theme, UseStylesProps>(theme => ({
  card: ({ fillContainer }) => {
    return {
      width: '100%',
      display: 'flex',
      height: fillContainer ? '100%' : undefined,
      flexDirection: 'column',
      background: theme.palette.background.default,
      boxShadow: '0px 2px 5px #00000029',
    };
  },
  bar: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: 'none',
    borderRight: 'none',
  },
  toolsBar: {
    height: '48px',
    display: 'flex',
    alignItems: 'center',
  },
  footer: {
    background: theme.palette.bkg.primary,
  },
  statusBar: ({ hasTools }) => ({
    height: '48px',
    width: '30%',
    borderLeft: hasTools ? 'none' : `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
  }),
}));

export const WbCard: FC<WbCardProps> = ({
  title,
  icon,
  actions,
  headerStyle,
  headerContentStyle,
  typographyProps,
  leading,
  titleContainerStyle,
  toolsComponents,
  statusComponent,
  cardClassName,
  children,
  cardStyle,
  footerStyle,
  toolsStyle,
  toolsBarStyle,
  footer,
  fillContainer,
  hideHeader = false,
  variant = 'default',
}) => {
  const classes = useStyles({
    hasTools: Boolean(toolsComponents),
    fillContainer: Boolean(fillContainer),
  });

  return (
    <Box className={clsx(classes.card, cardClassName)} style={cardStyle}>
      {!hideHeader && (
        <WbCardHeader
          title={title}
          icon={icon}
          actions={actions}
          style={headerStyle}
          contentStyle={headerContentStyle}
          typographyProps={typographyProps}
          leading={leading}
          titleContainerStyle={titleContainerStyle}
          variant={variant}
        />
      )}
      {!!toolsComponents || !!statusComponent ? (
        <Box className={classes.bar} style={toolsStyle}>
          {!!toolsComponents ? (
            <Box className={classes.toolsBar} style={toolsBarStyle}>
              {toolsComponents}
            </Box>
          ) : (
            <Box width="100%" />
          )}
          {!!statusComponent && (
            <Box className={classes.statusBar}>{statusComponent}</Box>
          )}
        </Box>
      ) : (
        <></>
      )}
      {children}
      {footer && (
        <Box className={classes.footer} style={footerStyle}>
          {footer}
        </Box>
      )}
    </Box>
  );
};
